import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Typography, 
  Table, 
  Input, 
  Button, 
  Space, 
  Avatar, 
  Empty, 
  Spin,
  message,
  Tabs,
  Alert,
  Divider
} from 'antd';
import { 
  SearchOutlined, 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined,
  CalendarOutlined,
  LinkOutlined,
  ExclamationCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import PatientService from '../../services/PatientService';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const PatientMatchModal = ({ 
  visible, 
  patient, 
  onCancel, 
  onMatchSuccess,
  eventId 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [potentialMatches, setPotentialMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [activeTab, setActiveTab] = useState('search');
  
  // Load potential matches when modal opens with a patient
  useEffect(() => {
    if (visible && patient) {
      loadPotentialMatches();
    }
  }, [visible, patient]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setSearchQuery('');
      setPotentialMatches([]);
      setSelectedMatch(null);
      setActiveTab('search');
    }
  }, [visible]);
  
  const loadPotentialMatches = async () => {
    if (!patient || !patient.emailAddress) return;
    
    setLoading(true);
    try {
      // Use email from the patient to find potential matches
      const response = await PatientService.searchPatients(patient.emailAddress, eventId);
      setPotentialMatches(response.data || []);
    } catch (error) {
      console.error('Error loading potential matches:', error);
      message.error('Failed to load potential patient matches');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      message.warning('Please enter a search term');
      return;
    }
    
    setLoading(true);
    try {
      const response = await PatientService.searchPatients(searchQuery, eventId);
      setPotentialMatches(response.data || []);
    } catch (error) {
      console.error('Error searching patients:', error);
      message.error('Failed to search patients');
    } finally {
      setLoading(false);
    }
  };
  const handleMatch = async () => {
    if (!patient || !selectedMatch) {
      message.warning('Please select a patient to match with');
      return;
    }
    
    setLoading(true);
    try {
      await PatientService.matchPatient(
        patient, // Pass the entire patient object - backend will extract patientId
        selectedMatch.patientId, // Use the patientId for existing patient
        eventId
      );
      
      message.success('Patient successfully matched to existing record');
      onMatchSuccess();
    } catch (error) {
      console.error('Error matching patient:', error);
      message.error('Failed to match patient');
    } finally {
      setLoading(false);
    }
  };
  
  const handleNeedsAttention = async () => {
    if (!patient) return;
    
    setLoading(true);
    try {
      await PatientService.markForAttention(
        patient._id,
        eventId,
        'Manual review required - potential duplicate'
      );
      
      message.success('Patient marked for manual review');
      onMatchSuccess();
    } catch (error) {
      console.error('Error marking patient for attention:', error);
      message.error('Failed to mark patient for attention');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendBackToForm = async () => {
    if (!patient) return;
    
    setLoading(true);
    try {
      await PatientService.sendBackToForm(
        patient._id,
        eventId,
        'Please complete the form with accurate information'
      );
      
      message.success('Patient sent back to complete the form');
      onMatchSuccess();
    } catch (error) {
      console.error('Error sending patient back to form:', error);
      message.error('Failed to send patient back to form');
    } finally {
      setLoading(false);
    }
  };
  
  const columns = [
    {
      title: 'Patient',
      key: 'patient',
      render: (record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <Text strong>
              {record.firstName} {record.lastName}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <CalendarOutlined /> DOB: {new Date(record.dateOfBirth).toLocaleDateString()}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (record) => (
        <Space direction="vertical" size="small">
          <Text>
            <MailOutlined /> {record.emailAddress}
          </Text>
          <Text>
            <PhoneOutlined /> {record.phoneNumber}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Last Visit',
      dataIndex: 'createdAt',
      key: 'lastVisit',
      render: (date) => (
        date ? new Date(date).toLocaleDateString() : 'No previous visits'
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Button 
          type={selectedMatch && selectedMatch._id === record._id ? 'primary' : 'default'}
          icon={<LinkOutlined />}
          onClick={() => setSelectedMatch(record)}
        >
          {selectedMatch && selectedMatch._id === record._id ? 'Selected' : 'Select'}
        </Button>
      ),
    },
  ];
  
  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          Patient Matching
          {patient && (
            <Text style={{ fontSize: '14px', marginLeft: '10px', fontWeight: 'normal' }}>
              - {patient.firstName} {patient.lastName}
            </Text>
          )}
        </Title>
      }
      visible={visible}
      onCancel={onCancel}
      width={800}
      footer={null}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <span>
              <SearchOutlined /> Search Existing Patients
            </span>
          } 
          key="search"
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space style={{ marginBottom: '16px', width: '100%' }}>
              <Input
                placeholder="Search by name, email, or phone"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onPressEnter={handleSearch}
                style={{ flex: 1 }}
                prefix={<SearchOutlined />}
                allowClear
              />
              <Button 
                type="primary" 
                onClick={handleSearch}
                loading={loading}
              >
                Search
              </Button>
            </Space>
            
            {potentialMatches.length > 0 ? (
              <>
                <Alert
                  message="Potential matches found"
                  description="Please review the potential matching patients below and select the correct match if the patient already exists in the system."
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
                
                <Table
                  columns={columns}
                  dataSource={potentialMatches}
                  rowKey="_id"
                  loading={loading}
                  pagination={{ pageSize: 5 }}
                  rowClassName={(record) => 
                    selectedMatch && selectedMatch._id === record._id ? 'selected-row' : ''
                  }
                />
                
                <div style={{ marginTop: '16px', textAlign: 'right' }}>
                  <Space>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button 
                      type="primary" 
                      disabled={!selectedMatch}
                      onClick={handleMatch}
                      loading={loading}
                    >
                      Match Patient
                    </Button>
                  </Space>
                </div>
              </>
            ) : (
              <Empty 
                description={
                  loading ? 'Searching for patients...' : (
                    searchQuery ? 'No matching patients found' : 'Search for existing patients'
                  )
                }
                image={loading ? <Spin size="large" /> : Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Space>
        </TabPane>
        
        <TabPane 
          tab={
            <span>
              <ExclamationCircleOutlined /> Actions
            </span>
          } 
          key="actions"
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              message="Patient Management Actions"
              description="Use these options if you need to handle special cases with this patient record."
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            
            <Divider orientation="left">Manual Review</Divider>
            <Paragraph>
              Mark this patient for manual review if you need more information or if there might be duplicates
              that require administrator attention.
            </Paragraph>
            <Button 
              type="primary" 
              icon={<WarningOutlined />}
              onClick={handleNeedsAttention}
              loading={loading}
              style={{ marginBottom: '16px' }}
            >
              Mark for Manual Review
            </Button>
            
            <Divider orientation="left">Send Back to Form</Divider>
            <Paragraph>
              Send the patient back to the registration form if information is incomplete or incorrect.
            </Paragraph>
            <Button 
              danger
              onClick={handleSendBackToForm}
              loading={loading}
            >
              Send Back to Form
            </Button>
          </Space>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default PatientMatchModal;
